import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, shareReplay, takeUntil, filter, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/services/auth.service';
import { Router, NavigationEnd, RouterLink, RouterOutlet } from '@angular/router';
import { OrgUser } from '../../models/user/user';
import { NavigationService } from '../../services/navigation.service';
import { OrganizationConfig, OrganizationConfigs } from '../../models/organization/config';
import { Organization } from '../../models/organization/organization';
import { MessagingService } from '../../services/messaging.service';

// ✅ Akita imports
import { UserQuery } from 'src/app/auth/state/user.query';
import { OrganizationQuery } from '../../state/organization/organization.query';
import { OrganizationConfigQuery } from '../../state/organization-config/organization-config.query';
import { UserOrganizationsQuery } from '../../state/user-organizations/user-organizations.query';
import { UserOrganizationsFacadeService } from '../../state/user-organizations/user-organizations-facade.service';
import { OrganizationFacadeService } from '../../state/organization/organization-facade.service';
import { MatSidenavContainer, MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatNavList, MatListItem, MatDivider } from '@angular/material/list';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ClickStopPropagationDirective } from '../../directives/click-stop-propagation.directive';
import { AsyncPipe } from '@angular/common';

type _moduleName =
  | 'warehouseOrders'
  | 'salesrep'
  | 'procurement'
  | 'expenses'
  | 'settings'
  | 'timesheets'
  | 'procurementOld';
type _route = { path: string; roles: string[]; name: string; moduleName: _moduleName };

interface VisibleRoute extends _route {
  visible: boolean;
}

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatSidenavContainer,
    MatSidenav,
    MatToolbar,
    MatFormField,
    MatSelect,
    MatOption,
    MatNavList,
    MatListItem,
    RouterLink,
    MatDivider,
    MatSidenavContent,
    MatIconButton,
    MatIcon,
    MatButton,
    ClickStopPropagationDirective,
    RouterOutlet,
    AsyncPipe,
  ],
})
export class NavigationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // ✅ Inject Akita queries and facades
  private breakpointObserver = inject(BreakpointObserver);
  private authService = inject(AuthService);
  private messagingService = inject(MessagingService);
  private navigationService = inject(NavigationService);
  private router = inject(Router);
  private userQuery = inject(UserQuery);
  private orgQuery = inject(OrganizationQuery);
  private orgConfigQuery = inject(OrganizationConfigQuery);
  private userOrganizationsQuery = inject(UserOrganizationsQuery);
  private userOrgsFacade = inject(UserOrganizationsFacadeService);
  private orgFacade = inject(OrganizationFacadeService);

  // ✅ Observables from Akita
  title$: Observable<string> = this.navigationService.getTitle();
  organization$: Observable<Organization> = this.orgQuery.organization$.pipe(
    distinctUntilChanged((prev, curr) => prev.id === curr.id) // Only emit when org actually changes
  );

  userOrganizations$: Observable<Organization[]> = this.userOrganizationsQuery.organizations$.pipe(
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
  );

  selectedOrg$: Observable<Organization> = this.orgQuery.organization$.pipe(
    distinctUntilChanged((prev, curr) => prev.id === curr.id)
  );

  // ✅ Computed observable for visible routes (prevents constant recalculation in template)
  visibleRoutes$: Observable<VisibleRoute[]>;

  // Component state
  showBackNavigation: boolean = true;

  routes: _route[] = [
    {
      path: 'warehouse-orders',
      roles: ['ADMIN', 'WAREHOUSEMANAGER', 'ACCOUNTANT', 'WAREHOUSECLERK'],
      name: 'Distribucija',
      moduleName: 'warehouseOrders',
    },
    {
      path: 'salesrep',
      roles: ['ADMIN', 'SALESREP'],
      name: 'Komercijala',
      moduleName: 'salesrep',
    },
    {
      path: 'procurement',
      roles: ['ADMIN', 'PROCUREMENTMANAGER'],
      name: 'Nabavka',
      moduleName: 'procurement',
    },
    {
      path: 'expenses',
      roles: ['ADMIN', 'PROCUREMENTMANAGER', 'SALESREP', 'WAREHOUSEMANAGER'],
      name: 'Troškovi',
      moduleName: 'expenses',
    },
    {
      path: 'users-management',
      roles: ['ADMIN'],
      name: 'Podešavanja',
      moduleName: 'settings',
    },
    {
      path: 'timesheets',
      roles: ['ADMIN'],
      name: 'Evidencija prisustva',
      moduleName: 'timesheets',
    },
  ];

  isHandset$: Observable<boolean>;

  constructor() {
    this.isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      map((result) => result.matches),
      distinctUntilChanged(), // Only emit when it actually changes
      shareReplay(1)
    );

    // ✅ Handle back navigation visibility
    // this.router.events.pipe(
    //   takeUntil(this.destroy$),
    //   filter(event => event instanceof NavigationEnd)
    // ).subscribe(event => {
    //   const navEvent = event as NavigationEnd;
    //   this.showBackNavigation = !navEvent.url.includes('customer-details/new-order');
    // });

    // ✅ Compute visible routes ONCE when orgUser or activeModules change
    this.visibleRoutes$ = combineLatest([
      this.userQuery.orgUser$.pipe(
        distinctUntilChanged(
          (prev, curr) => prev.id === curr.id && JSON.stringify(prev.roles) === JSON.stringify(curr.roles)
        )
      ),
      this.orgConfigQuery.activeModules$.pipe(
        debounceTime(100), // Wait for all config updates to settle
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      ),
    ]).pipe(
      map(([orgUser, activeModules]) => {
        return this.routes.map((route) => ({
          ...route,
          visible: this.checkVisibility(route, orgUser, activeModules),
        }));
      }),
      shareReplay(1) // Cache the result
    );
  }

  ngOnInit(): void {
    this.messagingService.requestPermission();
    this.messagingService.receiveMessage();

    // ✅ Initialize user organizations (only once)
    this.userOrgsFacade.loadUserOrganizations();
  }

  /**
   * Check if route is visible (pure function - no side effects)
   */
  private checkVisibility(route: _route, orgUser: OrgUser, activeModules: OrganizationConfigs.ActiveModules): boolean {
    // Check user role
    const hasRole = orgUser?.roles?.some((role) => route.roles.includes(role)) ?? false;
    if (!hasRole) return false;

    // Check if module is active
    return !!activeModules[route.moduleName];
  }

  signOut() {
    this.authService.clearAllStatesAndSignout();
  }

  navigateBack() {
    this.navigationService.goBack();
  }

  compareOrgs(org1: any, org2: any): boolean {
    return org1?.id === org2?.id;
  }

  /**
   * Switch organization and reload
   */
  async changeOrg(selectedOrg: Organization) {
    if (!selectedOrg?.id) {
      console.warn('⚠️ No organization selected');
      return;
    }

    console.log('🔄 Switching organization to:', selectedOrg.name);

    try {
      const user = this.userQuery.getValue().user;
      await this.orgFacade.switchOrganization(selectedOrg.id, user?.id, user?.orgIds);
      window.location.reload();
    } catch (error) {
      console.error('❌ Failed to switch organization:', error);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
